import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useUniverse } from '../../hooks/useChancellery';
import { ConfirmModal } from '../../components/ConfirmModal';
import { AddTierModal } from '../../components/chancellery/AddTierModal';
import { CreateNextAgeModal } from '../../components/chancellery/CreateNextAgeModal';
import { COLORS, INPUT_STYLE } from '../../constants/theme';
import type { AgeStatus, NextAge, Universe } from '../../types/chancellery';

const AGE_STATUS_META: Record<AgeStatus, { label: string; color: string; backgroundColor: string }> = {
  ACTIVE: { label: 'Active', color: COLORS.positive, backgroundColor: '#0f2a1a' },
  PAUSED: { label: 'Paused', color: COLORS.warning, backgroundColor: '#3a2f10' },
  PENDING: { label: 'Pending', color: COLORS.text.tertiary, backgroundColor: COLORS.sectionHeader },
};

type AgeActionKey = 'pause' | 'resume' | 'reset' | 'start' | 'reset-sandbox' | 'end';

interface AgeActionConfig {
  title: string;
  body: string;
  confirmLabel: string;
  requireTyped: boolean;
  typedKeyword: string;
  successHeadline: string;
  successBody: string;
}

function buildActionConfig(
  key: AgeActionKey,
  universe: Universe,
  scheduleChoice?: { withSchedule: boolean; days: number }
): AgeActionConfig {
  const { currentAge, nextAge, sandbox, worlds, name } = universe;
  const tierCount = worlds.length;
  const tierWord = tierCount === 1 ? 'tier' : 'tiers';
  const isAgePopulated = worlds.some((w) => w.kingdomCount > 0);
  const hasSchedule = currentAge.totalDays !== null;

  switch (key) {
    case 'pause':
      return {
        title: `Pause Age ${currentAge.ageNumber}?`,
        body: hasSchedule
          ? `This freezes ${name}'s clock — every tier (Div 1–${tierCount}) stops advancing until resumed. The scheduled end date shifts out by however long it stays paused.`
          : `This freezes ${name}'s clock — every tier (Div 1–${tierCount}) stops advancing until resumed.`,
        confirmLabel: 'Pause Age',
        requireTyped: false,
        typedKeyword: '',
        successHeadline: `Age ${currentAge.ageNumber} would pause`,
        successBody:
          'Pause would take effect — no data was changed. Resume any time; the end date would adjust automatically to account for the paused time.',
      };
    case 'resume':
      return {
        title: `Resume Age ${currentAge.ageNumber}?`,
        body: hasSchedule
          ? `This unfreezes ${name}'s clock — every tier starts advancing again from today. The scheduled end date shifts out by however long Age ${currentAge.ageNumber} stayed paused.`
          : `This unfreezes ${name}'s clock — every tier starts advancing again from today.`,
        confirmLabel: 'Resume Age',
        requireTyped: false,
        typedKeyword: '',
        successHeadline: `Age ${currentAge.ageNumber} would resume`,
        successBody:
          'Resume would take effect — no data was changed. Every tier would advance again, with the end date recalculated to account for the paused time.',
      };
    case 'reset':
      return {
        title: `Reset Age ${currentAge.ageNumber}?`,
        body: hasSchedule
          ? `This wipes every kingdom across all ${tierCount} ${tierWord} of ${name} and restarts Age ${currentAge.ageNumber}'s clock from today. The Utopian-time length stays the same — only the end date changes. This cannot be undone.`
          : `This wipes every kingdom across all ${tierCount} ${tierWord} of ${name} and restarts Age ${currentAge.ageNumber}'s clock from today. This cannot be undone.`,
        confirmLabel: 'Reset Age',
        requireTyped: isAgePopulated,
        typedKeyword: 'Reset',
        successHeadline: `Age ${currentAge.ageNumber} would be reset`,
        successBody: `Reset would complete — no data was changed. All ${tierCount} ${tierWord} would restart from day 0, with a new end date recalculated from today.`,
      };
    case 'start': {
      const scheduleBody = scheduleChoice
        ? scheduleChoice.withSchedule
          ? ` with a ${scheduleChoice.days}-real-day schedule`
          : ' with no schedule, running until manually ended'
        : '';
      return {
        title: `Start Age ${nextAge?.ageNumber} now?`,
        // Corrected in Phase 3 (Decision 4 / Critical #6): the real startAge unconditionally
        // rejects with AGE_ALREADY_ACTIVE while any age is ACTIVE -- it has no end-then-start
        // path. Whether it should gain one is SF-110's decision, not this spec's; this button
        // stays simulated, only the promised precondition is corrected.
        body: `This starts Age ${nextAge?.ageNumber} for every tier of ${name} immediately, applies this age's promotion and relegation swaps, and locks Age ${nextAge?.ageNumber}'s properties from further editing. Age ${currentAge.ageNumber} must already be ended — starting Age ${nextAge?.ageNumber} while it's still active is rejected.`,
        confirmLabel: 'Start Age',
        requireTyped: false,
        typedKeyword: '',
        successHeadline: `Age ${nextAge?.ageNumber} would start`,
        successBody: `Start would take effect — no data was changed. Promotion and relegation would apply across all tiers, Age ${currentAge.ageNumber} would end, and Age ${nextAge?.ageNumber} would start${scheduleBody}.`,
      };
    }
    case 'end': {
      const daysRemaining = currentAge.totalDays !== null ? currentAge.totalDays - currentAge.daysElapsed : null;
      return {
        title: `End Age ${currentAge.ageNumber} now?`,
        body: `This ends Age ${currentAge.ageNumber} for every tier of ${name} immediately`
          + (daysRemaining !== null ? `, ${daysRemaining} days before its scheduled end` : '')
          + '. There is no way to un-end an age. This cannot be undone.',
        confirmLabel: 'End Age',
        requireTyped: isAgePopulated,
        typedKeyword: 'End',
        successHeadline: `Age ${currentAge.ageNumber} would end`,
        successBody: 'End would take effect — no data was changed.',
      };
    }
    case 'reset-sandbox': {
      const isSandboxPopulated = (sandbox?.testerCount ?? 0) > 0;
      return {
        title: `Reset ${sandbox?.name}?`,
        body: "This wipes the sandbox's test kingdoms and restarts it fresh. Testers keep their whitelist access. This cannot be undone.",
        confirmLabel: 'Reset Sandbox',
        requireTyped: isSandboxPopulated,
        typedKeyword: 'Reset',
        successHeadline: `${sandbox?.name} would be reset`,
        successBody:
          'Reset would complete — no data was changed. Test kingdoms would clear; whitelisted testers could rejoin immediately.',
      };
    }
  }
}

type ScheduleChoiceValue = 'WITH_SCHEDULE' | 'NO_SCHEDULE';

const SCHEDULE_OPTIONS: Array<{ value: ScheduleChoiceValue; label: string }> = [
  { value: 'WITH_SCHEDULE', label: 'Set a schedule' },
  { value: 'NO_SCHEDULE', label: 'No schedule (runs until manually ended)' },
];

function formatProjectedEndDate(days: number): string {
  return new Date(Date.now() + days * 86_400_000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

interface StartScheduleChoiceProps {
  startsWithSchedule: boolean;
  onSelect: (value: ScheduleChoiceValue) => void;
  scheduleDaysInput: string;
  onScheduleDaysInputChange: (value: string) => void;
  scheduleInputBlockedReason: string | null;
  projectedEndDateLabel: string | null;
}

/** Embedded in the Start ConfirmModal's children (Component 11c) -- roving-tabIndex radiogroup
 *  mirroring CreateNextAgeModal's own pattern. */
function StartScheduleChoice({
  startsWithSchedule,
  onSelect,
  scheduleDaysInput,
  onScheduleDaysInputChange,
  scheduleInputBlockedReason,
  projectedEndDateLabel,
}: StartScheduleChoiceProps) {
  const radioRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, idx: number) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (idx + 1) % SCHEDULE_OPTIONS.length;
      onSelect(SCHEDULE_OPTIONS[next].value);
      radioRefs.current[next]?.focus();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = (idx - 1 + SCHEDULE_OPTIONS.length) % SCHEDULE_OPTIONS.length;
      onSelect(SCHEDULE_OPTIONS[prev].value);
      radioRefs.current[prev]?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(SCHEDULE_OPTIONS[idx].value);
    }
  };

  return (
    <div className="mt-4">
      <div id="start-schedule-label" className="text-sm font-medium mb-2" style={{ color: COLORS.text.tertiary }}>
        Schedule
      </div>
      <div role="radiogroup" aria-labelledby="start-schedule-label" className="flex flex-col gap-2">
        {SCHEDULE_OPTIONS.map((option, idx) => {
          const isSelected = (option.value === 'WITH_SCHEDULE') === startsWithSchedule;
          return (
            <button
              key={option.value}
              ref={(el) => { radioRefs.current[idx] = el; }}
              role="radio"
              aria-checked={isSelected}
              tabIndex={isSelected ? 0 : -1}
              type="button"
              onClick={() => onSelect(option.value)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
              className="rounded px-3 py-2 text-sm text-left transition-colors"
              style={{
                backgroundColor: isSelected ? COLORS.sectionHeader : COLORS.card,
                border: `1px solid ${isSelected ? COLORS.link : COLORS.border}`,
                color: isSelected ? COLORS.text.primary : COLORS.text.secondary,
                outline: 'none',
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      {startsWithSchedule && (
        <div className="mt-3">
          <label htmlFor="start-schedule-days-input" className="block mb-1.5 text-sm" style={{ color: COLORS.text.tertiary }}>
            Real days
          </label>
          <input
            id="start-schedule-days-input"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={scheduleDaysInput}
            onChange={(e) => onScheduleDaysInputChange(e.target.value)}
            aria-invalid={!!scheduleInputBlockedReason}
            aria-describedby={
              scheduleInputBlockedReason
                ? 'start-schedule-days-error'
                : projectedEndDateLabel
                  ? 'start-schedule-days-projection'
                  : undefined
            }
            className="w-full px-3 py-2 rounded focus:outline-none"
            style={INPUT_STYLE}
          />
          {scheduleInputBlockedReason ? (
            <p id="start-schedule-days-error" className="mt-1 text-xs" style={{ color: COLORS.negative }}>
              {scheduleInputBlockedReason}
            </p>
          ) : projectedEndDateLabel ? (
            <p id="start-schedule-days-projection" className="mt-1 text-xs" style={{ color: COLORS.text.tertiary }}>
              {projectedEndDateLabel}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

interface SuccessDialogProps {
  headline: string;
  body: string;
  onDone: () => void;
  /** New (SF-108). When provided, the cleanup effect focuses this element instead of the
   *  auto-captured document.activeElement-at-mount trigger. Needed whenever the dialog's own
   *  trigger element unmounts before or during the dialog's own mount (as Create Next Age's does)
   *  — omitted by all 5 existing sibling call sites, which are unaffected. */
  focusOnCloseRef?: React.RefObject<HTMLElement>;
}

/** Minimal single-button success step shown after a confirm action (Decision 6: simulated copy). */
function SuccessDialog({ headline, body, onDone, focusOnCloseRef }: SuccessDialogProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);

  useEffect(() => {
    triggerRef.current = document.activeElement;
    modalRef.current?.querySelector<HTMLElement>('button')?.focus();
    // Captured once, at mount time -- focusOnCloseRef is an external prop-controlled ref, so its
    // .current could in principle change by the time this cleanup runs; the target element (the
    // Next Age card) stays mounted for this dialog's whole lifetime, so capturing it now is both
    // correct and what quiets the exhaustive-deps ref-in-cleanup warning.
    const focusOnCloseTarget = focusOnCloseRef?.current;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onDone();
        return;
      }
      // Single-button dialog — Tab/Shift+Tab always stays on the Done button.
      if (e.key === 'Tab') {
        e.preventDefault();
        modalRef.current?.querySelector<HTMLElement>('button')?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const restoreTarget = focusOnCloseTarget ?? triggerRef.current;
      if (restoreTarget instanceof HTMLElement) {
        restoreTarget.focus();
      }
    };
  }, [onDone, focusOnCloseRef]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="chancellery-success-title"
        className="rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        <div className="flex items-start gap-3">
          <span aria-hidden="true" style={{ color: COLORS.positive, fontSize: 20 }}>✓</span>
          <div>
            <div id="chancellery-success-title" className="text-base font-semibold mb-1" style={{ color: COLORS.text.primary }}>
              {headline}
            </div>
            <p className="text-sm" style={{ color: COLORS.text.tertiary }}>{body}</p>
          </div>
        </div>
        <div className="flex justify-end mt-6">
          <button
            type="button"
            onClick={onDone}
            className="px-4 py-2 rounded text-sm font-medium"
            style={{ backgroundColor: COLORS.btnPrimary, color: COLORS.text.primary }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

/** Chancellery universe detail — route: /chancellery/universes/:universeId */
export function UniverseDetail() {
  const { universeId } = useParams<{ universeId: string }>();
  const navigate = useNavigate();
  const { data: universe } = useUniverse(universeId);

  const [confirmActionKey, setConfirmActionKey] = useState<AgeActionKey | null>(null);
  const [confirmPhase, setConfirmPhase] = useState<'confirm' | 'success'>('confirm');
  const [isAddTierOpen, setIsAddTierOpen] = useState(false);
  const [editingBoundaryWorldId, setEditingBoundaryWorldId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [swapOverrides, setSwapOverrides] = useState<Record<string, number>>({});

  // SF-108: Create Next Age (simulated) local state.
  const [isCreateNextAgeOpen, setIsCreateNextAgeOpen] = useState(false);
  const [createdNextAge, setCreatedNextAge] = useState<NextAge | null>(null);
  const [showCreateSuccess, setShowCreateSuccess] = useState(false);
  const nextAgeCardRef = useRef<HTMLDivElement>(null);

  // SF-110: Start Age schedule choice (simulated) local state — reset whenever the Start button
  // opens the confirm, via openStartConfirm below (Component 11c).
  const [startsWithSchedule, setStartsWithSchedule] = useState(true);
  const [scheduleDaysInput, setScheduleDaysInput] = useState('84');

  if (!universe) {
    return (
      <div>
        <Link to="/chancellery" className="text-sm" style={{ color: COLORS.link }}>
          ← Universes
        </Link>
        <p className="mt-4 text-sm" style={{ color: COLORS.text.tertiary }}>
          Universe not found.
        </p>
      </div>
    );
  }

  const closeConfirm = () => {
    setConfirmActionKey(null);
    setConfirmPhase('confirm');
  };

  // SF-108: computed once, read everywhere "the next age" is needed on this page (Component 7) —
  // never read universe.nextAge directly below this line.
  const resolvedNextAge = universe.nextAge ?? createdNextAge;

  // SF-110: Component 11c invalid-input contract — null iff "No schedule" is chosen, or "Set a
  // schedule" is chosen with a whole number in [1, 365] (matches StartAgeRequest's @Positive @Max(365)).
  const trimmedScheduleDaysInput = scheduleDaysInput.trim();
  const scheduleDaysNumeric = Number(trimmedScheduleDaysInput);
  const isWholeScheduleDaysInput = /^\d+$/.test(trimmedScheduleDaysInput);
  const scheduleInputBlockedReason: string | null =
    !startsWithSchedule
      ? null
      : isWholeScheduleDaysInput && scheduleDaysNumeric >= 1 && scheduleDaysNumeric <= 365
        ? null
        : 'Enter a whole number of real days between 1 and 365.';
  const projectedEndDateLabel =
    startsWithSchedule && !scheduleInputBlockedReason
      ? `ends around ${formatProjectedEndDate(scheduleDaysNumeric)}`
      : null;

  const activeConfig = confirmActionKey
    ? buildActionConfig(
        confirmActionKey,
        { ...universe, nextAge: resolvedNextAge },
        confirmActionKey === 'start' ? { withSchedule: startsWithSchedule, days: scheduleDaysNumeric } : undefined
      )
    : null;
  const sortedWorlds = [...universe.worlds].sort((a, b) => a.divRank - b.divRank);

  const startEditingBoundary = (worldId: string, currentPercent: number) => {
    setEditingBoundaryWorldId(worldId);
    setEditValue(String(currentPercent));
  };

  const saveBoundary = (worldId: string) => {
    const parsed = Number(editValue);
    if (Number.isFinite(parsed)) {
      setSwapOverrides((prev) => ({ ...prev, [worldId]: parsed }));
    }
    setEditingBoundaryWorldId(null);
  };

  const openStartConfirm = () => {
    setStartsWithSchedule(true);
    setScheduleDaysInput('84');
    setConfirmActionKey('start');
  };

  // SF-110: unified 4-way discriminant (Component 11c, Decision 7) — the single source every
  // Current Age card region (days-left stat, progress bar, caption) reads from.
  type AgeCardVariant = 'pending' | 'paused' | 'active-scheduled' | 'active-open-ended';
  const ageCardVariant: AgeCardVariant =
    universe.currentAge.status === 'PENDING'
      ? 'pending'
      : universe.currentAge.status === 'PAUSED'
        ? 'paused'
        : universe.currentAge.totalDays !== null
          ? 'active-scheduled'
          : 'active-open-ended';

  const currentAge = universe.currentAge;
  const currentAgeCaption =
    ageCardVariant === 'pending'
      ? 'Not yet started'
      : ageCardVariant === 'active-open-ended'
        ? `Day ${currentAge.daysElapsed} · no scheduled end`
        : `Day ${currentAge.daysElapsed} of ${currentAge.totalDays}${ageCardVariant === 'paused' ? ' elapsed before pause' : ''} · started ${currentAge.startedAtLabel}${ageCardVariant === 'active-scheduled' && currentAge.endsAtLabel ? ` · ends ${currentAge.endsAtLabel}` : ''}`;

  return (
    <div>
      <Link to="/chancellery" className="text-sm" style={{ color: COLORS.link }}>
        ← Universes
      </Link>

      <div className="flex items-start justify-between mt-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold mb-1" style={{ color: COLORS.text.primary }}>
            {universe.name}
          </h1>
          <p className="text-sm" style={{ color: COLORS.text.tertiary }}>
            {universe.description}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setIsAddTierOpen(true)}
          className="px-4 py-2 rounded font-medium text-sm"
          style={{ backgroundColor: COLORS.btnPrimary, color: COLORS.text.primary }}
        >
          + Add Tier
        </button>
      </div>

      {/* Current Age */}
      <div className="rounded p-5 mb-4" style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}>
        <div className="text-xs uppercase tracking-wide mb-2" style={{ color: COLORS.text.tertiary }}>
          Current Age · applies to every tier
        </div>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-base font-semibold" style={{ color: COLORS.text.primary }}>
            Age {universe.currentAge.ageNumber} — {universe.currentAge.name}
          </span>
          <span
            className="px-2 py-0.5 rounded text-xs font-semibold"
            style={{
              backgroundColor: AGE_STATUS_META[universe.currentAge.status].backgroundColor,
              color: AGE_STATUS_META[universe.currentAge.status].color,
            }}
          >
            {AGE_STATUS_META[universe.currentAge.status].label}
          </span>
        </div>

        {ageCardVariant === 'paused' && (
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-2xl font-semibold" style={{ color: COLORS.warning }}>Paused</span>
            <span className="text-sm" style={{ color: COLORS.text.tertiary }}>
              {currentAge.pausedDaysAgo} days ago
            </span>
          </div>
        )}
        {ageCardVariant === 'active-scheduled' && (
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-2xl font-semibold" style={{ color: COLORS.text.primary }}>
              {(currentAge.totalDays as number) - currentAge.daysElapsed}
            </span>
            <span className="text-sm" style={{ color: COLORS.text.tertiary }}>days left</span>
          </div>
        )}

        {/* SF-110: only paused/active-scheduled ever render the progress bar — structurally
         *  absent for pending/open-ended, which have no meaningful total to bound it by. */}
        {(ageCardVariant === 'paused' || ageCardVariant === 'active-scheduled') && (
          <div
            role="progressbar"
            aria-valuenow={currentAge.daysElapsed}
            aria-valuemin={0}
            aria-valuemax={currentAge.totalDays as number}
            aria-label={`Age ${currentAge.ageNumber} progress`}
            className="h-1.5 rounded-full overflow-hidden mb-2"
            style={{ backgroundColor: COLORS.cardAlt }}
          >
            <div
              className="h-full"
              style={{
                width: `${Math.round((currentAge.daysElapsed / (currentAge.totalDays as number)) * 100)}%`,
                backgroundColor: ageCardVariant === 'paused' ? COLORS.warning : COLORS.link,
              }}
            />
          </div>
        )}

        <div className="text-xs mb-3" style={{ color: COLORS.text.tertiary }}>
          {currentAgeCaption}
        </div>

        <div className="flex gap-2">
          {currentAge.status === 'ACTIVE' && (
            <button
              type="button"
              onClick={() => setConfirmActionKey('pause')}
              className="px-3 py-1.5 rounded text-sm"
              style={{ backgroundColor: COLORS.cardAlt, color: COLORS.text.secondary, border: `1px solid ${COLORS.border}` }}
            >
              Pause
            </button>
          )}
          {currentAge.status === 'PAUSED' && (
            <button
              type="button"
              onClick={() => setConfirmActionKey('resume')}
              className="px-3 py-1.5 rounded text-sm"
              style={{ backgroundColor: COLORS.cardAlt, color: COLORS.text.secondary, border: `1px solid ${COLORS.border}` }}
            >
              Resume
            </button>
          )}
          <button
            type="button"
            onClick={() => setConfirmActionKey('reset')}
            aria-label={`Reset Age ${currentAge.ageNumber}`}
            className="px-3 py-1.5 rounded text-sm"
            style={{ backgroundColor: COLORS.cardAlt, color: COLORS.negative, border: `1px solid ${COLORS.border}` }}
          >
            Reset
          </button>
          {(currentAge.status === 'ACTIVE' || currentAge.status === 'PAUSED') && (
            <button
              type="button"
              onClick={() => setConfirmActionKey('end')}
              aria-label={`End Age ${currentAge.ageNumber}`}
              className="px-3 py-1.5 rounded text-sm"
              style={{ backgroundColor: COLORS.cardAlt, color: COLORS.negative, border: `1px solid ${COLORS.border}` }}
            >
              End Age
            </button>
          )}
        </div>
      </div>

      {/* Next Age */}
      {resolvedNextAge && (
        <div
          ref={nextAgeCardRef}
          tabIndex={-1}
          className="rounded p-5 mb-6 focus-visible:outline focus-visible:outline-2"
          style={{ backgroundColor: COLORS.card, border: `1px dashed ${COLORS.border}`, outlineColor: COLORS.link }}
        >
          <div className="text-xs uppercase tracking-wide mb-2" style={{ color: COLORS.text.tertiary }}>
            Next Age · universe-wide
          </div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-base font-semibold" style={{ color: COLORS.text.primary }}>
              Age {resolvedNextAge.ageNumber}
            </span>
            <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ backgroundColor: COLORS.sectionHeader, color: COLORS.text.tertiary }}>
              Pending
            </span>
          </div>
          <p className="text-sm mb-3" style={{ color: COLORS.text.tertiary }}>
            Starts {resolvedNextAge.startsAtLabel} — promotion and relegation apply the moment it begins.
          </p>
          <button
            type="button"
            onClick={openStartConfirm}
            className="px-3 py-1.5 rounded text-sm"
            style={{ backgroundColor: COLORS.cardAlt, color: COLORS.text.secondary, border: `1px solid ${COLORS.border}` }}
          >
            Start Age {resolvedNextAge.ageNumber} now
          </button>
        </div>
      )}

      {/* Empty-state CTA: no age queued behind the current one (Component 7, Critical #9) */}
      {!resolvedNextAge && universe.currentAge.status !== 'PENDING' && (
        <div className="rounded p-5 mb-6" style={{ backgroundColor: COLORS.card, border: `1px dashed ${COLORS.border}` }}>
          <div className="text-xs uppercase tracking-wide mb-2" style={{ color: COLORS.text.tertiary }}>
            Next Age · universe-wide
          </div>
          <p className="text-sm mb-3" style={{ color: COLORS.text.tertiary }}>
            Nothing queued. When Age {universe.currentAge.ageNumber} ends, there's no Age{' '}
            {universe.currentAge.ageNumber + 1} to follow it.
          </p>
          <button
            type="button"
            onClick={() => setIsCreateNextAgeOpen(true)}
            className="px-3 py-1.5 rounded text-sm font-medium"
            style={{ backgroundColor: COLORS.btnPrimary, color: COLORS.text.primary }}
          >
            + Create Next Age
          </button>
        </div>
      )}

      <div className="text-xs uppercase tracking-wide mb-2" style={{ color: COLORS.text.tertiary }}>
        Tiers, ranked
      </div>
      <div className="rounded overflow-hidden mb-6" style={{ border: `1px solid ${COLORS.border}` }}>
        {sortedWorlds.map((world, index) => {
          const isTop = index === 0;
          const worldBelow = sortedWorlds[index + 1];
          const swapPercent = worldBelow
            ? swapOverrides[worldBelow.worldId] ?? worldBelow.boundaryAbove?.swapPercent
            : undefined;
          return (
            <div key={world.worldId}>
              <button
                type="button"
                onClick={() => navigate(`/chancellery/universes/${universe.universeId}/worlds/${world.worldId}`)}
                className="w-full flex items-center justify-between px-4 py-3 text-left transition-colors hover:bg-white/5"
                style={{ backgroundColor: COLORS.card, borderBottom: `1px solid ${COLORS.border}` }}
              >
                <div className="flex items-center gap-3">
                  <span
                    className="text-xs font-bold px-2 py-1 rounded"
                    style={
                      isTop
                        ? { backgroundColor: 'rgba(106,158,142,0.15)', color: COLORS.link }
                        : { color: COLORS.text.tertiary }
                    }
                  >
                    DIV {world.divRank}
                  </span>
                  <span className="text-sm font-medium" style={{ color: COLORS.text.primary }}>
                    {world.name}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs" style={{ color: COLORS.text.tertiary }}>
                    {world.kingdomCount} kingdoms
                  </span>
                  <span aria-hidden="true" style={{ color: COLORS.text.tertiary }}>›</span>
                </div>
              </button>

              {index < sortedWorlds.length - 1 && swapPercent !== undefined && (
                <div
                  className="flex items-center gap-3 px-4 py-2 text-xs"
                  style={{ backgroundColor: COLORS.bgBar, borderBottom: `1px solid ${COLORS.border}`, color: COLORS.text.tertiary }}
                >
                  {editingBoundaryWorldId === sortedWorlds[index + 1].worldId ? (
                    <>
                      <label htmlFor={`swap-pct-${sortedWorlds[index + 1].worldId}`} className="sr-only">
                        Promotion and relegation swap percentage between Div {world.divRank} and Div {sortedWorlds[index + 1].divRank}
                      </label>
                      <input
                        id={`swap-pct-${sortedWorlds[index + 1].worldId}`}
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="w-16 px-2 py-1 rounded focus:outline-none"
                        style={INPUT_STYLE}
                      />
                      <span>%</span>
                      <button
                        type="button"
                        onClick={() => saveBoundary(sortedWorlds[index + 1].worldId)}
                        className="px-2 py-1 rounded"
                        style={{ backgroundColor: COLORS.cardAlt, color: COLORS.text.secondary, border: `1px solid ${COLORS.border}` }}
                      >
                        Save
                      </button>
                    </>
                  ) : (
                    <>
                      <span>{swapPercent}% swap each age</span>
                      <button
                        type="button"
                        onClick={() => startEditingBoundary(sortedWorlds[index + 1].worldId, swapPercent)}
                        aria-label={`Edit promotion and relegation swap percentage between Div ${world.divRank} and Div ${sortedWorlds[index + 1].divRank}`}
                        className="px-2 py-1 rounded"
                        style={{ backgroundColor: 'transparent', color: COLORS.text.tertiary, border: 'none', cursor: 'pointer' }}
                      >
                        Edit
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {universe.sandbox && (
        <div className="rounded p-5" style={{ backgroundColor: COLORS.card, border: `1px dashed ${COLORS.link}` }}>
          <div className="text-xs uppercase tracking-wide mb-1" style={{ color: COLORS.link }}>
            {/* SF-108: reads resolvedNextAge, not universe.nextAge directly (Component 7 fix) --
                unfalsifiable under simulation by construction (createdNextAge.ageNumber is always
                currentAge.ageNumber + 1, the same value this line's own fallback branch already
                produces), but a real latent trap once this page's data goes real. */}
            Sandbox · testing Age {resolvedNextAge ? resolvedNextAge.ageNumber : universe.currentAge.ageNumber + 1}&apos;s rules
          </div>
          <div className="text-base font-semibold mb-1" style={{ color: COLORS.text.primary }}>
            {universe.sandbox.name}
          </div>
          <p className="text-sm mb-3" style={{ color: COLORS.text.tertiary }}>
            {universe.sandbox.testerCount} accounts testing · properties open for tuning · not part of the ranked ladder
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className="px-3 py-1.5 rounded text-sm"
              style={{ backgroundColor: COLORS.cardAlt, color: COLORS.text.secondary, border: `1px solid ${COLORS.border}` }}
            >
              Manage whitelist
            </button>
            <button
              type="button"
              onClick={() => setConfirmActionKey('reset-sandbox')}
              aria-label={`Reset ${universe.sandbox.name}`}
              className="px-3 py-1.5 rounded text-sm"
              style={{ backgroundColor: COLORS.cardAlt, color: COLORS.text.secondary, border: `1px solid ${COLORS.border}` }}
            >
              Reset
            </button>
          </div>
        </div>
      )}

      {activeConfig && confirmPhase === 'confirm' && (
        <ConfirmModal
          isOpen
          title={activeConfig.title}
          onCancel={closeConfirm}
          onConfirm={() => setConfirmPhase('success')}
          confirmText={activeConfig.confirmLabel}
          requireTypedConfirmation={activeConfig.requireTyped ? { keyword: activeConfig.typedKeyword } : undefined}
          confirmDisabled={confirmActionKey === 'start' ? !!scheduleInputBlockedReason : undefined}
        >
          <p>{activeConfig.body}</p>
          {confirmActionKey === 'start' && (
            <StartScheduleChoice
              startsWithSchedule={startsWithSchedule}
              onSelect={(value) => setStartsWithSchedule(value === 'WITH_SCHEDULE')}
              scheduleDaysInput={scheduleDaysInput}
              onScheduleDaysInputChange={setScheduleDaysInput}
              scheduleInputBlockedReason={scheduleInputBlockedReason}
              projectedEndDateLabel={projectedEndDateLabel}
            />
          )}
        </ConfirmModal>
      )}
      {activeConfig && confirmPhase === 'success' && (
        <SuccessDialog headline={activeConfig.successHeadline} body={activeConfig.successBody} onDone={closeConfirm} />
      )}

      {showCreateSuccess && (
        <SuccessDialog
          headline={`Age ${createdNextAge?.ageNumber} would be created`}
          body={`Create would take effect — no data was changed. Age ${createdNextAge?.ageNumber} would join the queue as the universe's next age.`}
          focusOnCloseRef={nextAgeCardRef}
          onDone={() => setShowCreateSuccess(false)}
        />
      )}

      <AddTierModal
        isOpen={isAddTierOpen}
        onClose={() => setIsAddTierOpen(false)}
        newDivRank={sortedWorlds.length + 1}
        universeName={universe.name}
      />

      <CreateNextAgeModal
        isOpen={isCreateNextAgeOpen}
        onClose={() => setIsCreateNextAgeOpen(false)}
        currentAgeNumber={universe.currentAge.ageNumber}
        onCreated={(nextAge) => {
          setCreatedNextAge(nextAge);
          setIsCreateNextAgeOpen(false);
          setShowCreateSuccess(true);
        }}
      />
    </div>
  );
}
